/**
 * Willow Interiors — Homepage Content
 *
 * Edit this file to update homepage text, CTAs, FAQs, process steps,
 * scope items, product categories, and B2B/NRI copy.
 * No component architecture changes required.
 */

import { findProjectPhoto } from '@/data/projectPhotos';
import { GUBBI_GUDU, PROJECT_STORIES } from '@/data/projectStories';

// ─── HERO ────────────────────────────────────────────────────────────────────

export const HERO = {
  label: 'DESIGN · MANUFACTURE · INSTALL',
  location: 'BENGALURU · KARNATAKA',
  // Homepage owns "interior designers in Bangalore"; /home-interiors owns "complete home interiors"
  h1Line1: 'Interior Designers in Bangalore.',
  h1Line2: 'Homes Designed to Be Built.',
  // A hard, checkable claim (review Oct 2026): own factory + one team, both facts shown further down the page
  body: 'Our own factory makes your kitchen, wardrobes and furniture, and one team takes your home from design to handover.',
  // Strongest client proof, shown under the buttons — a real review (Pooja Rajak), quoted, never reworded
  proof: {
    quote: 'The kitchen was ready within 3 days of taking measurements.',
    by: 'Pooja Rajak, 3BHK, Varthur, Bangalore',
    href: '#reviews',
  },
  ctaPrimary: { label: 'START YOUR PROJECT', href: '#enquiry' },
  ctaSecondary: { label: 'VIEW PROJECTS', href: '/projects' },
  image: '/assets/images/home/living-room-with-floating-tv-unit-and-fluted-wood-panel.jpg',
  imageAlt: 'Living room of Gubbi Gudu, a G+4 villa in Anjanapura, Bangalore, by Willow Interiors — floating walnut TV unit with fluted wood wall panel',
  // Caption on the hero photo — it is a real completed project, linked so visitors can see the rest
  imageCredit: {
    label: 'Real Willow project — photographed on site',
    cta: 'View this project',
    href: '/projects/gubbi-gudu-villa-anjanapura',
  },
};

// ─── SECTION 01 — PROCESS ────────────────────────────────────────────────────

export const PROCESS_SECTION = {
  sectionNumber: '01',
  sectionLabel: 'THE PROCESS',
  h2: 'From Design to Handover.',
  body: 'We bring the major stages of an interior project into one coordinated process — from design and material selection to manufacturing, installation, quality checks and handover.',
  designerNote: 'Already have your own designer or design? We can work with that too.',
  cta: { label: 'Explore How Home Interiors Work →', href: '/home-interiors' },
  // icon: key into PROCESS_ICONS in page.tsx. desc: one line under each card.
  steps: [
    { id: 'understand', label: 'UNDERSTAND', icon: 'grid', desc: 'Your space, needs and possibilities.' },
    { id: 'design', label: 'DESIGN', icon: 'sofa', desc: 'Practical layouts that fit your life.' },
    { id: 'material', label: 'MATERIAL SELECTION', icon: 'documents', desc: 'Durable materials, chosen with you.' },
    { id: 'documentation', label: 'DOCUMENTATION', icon: 'document', desc: 'Build-ready drawings and specifications.' },
    { id: 'manufacturing', label: 'MANUFACTURING', icon: 'cog', desc: 'Cut, pressed and edge-banded at our own unit.' },
    { id: 'installation', label: 'INSTALLATION', icon: 'wrench', desc: 'Skilled teams and on-site execution.' },
    { id: 'qc', label: 'QC', icon: 'shield', desc: 'Multiple checks for lasting quality.' },
    { id: 'handover', label: 'HANDOVER', icon: 'key', desc: 'A clean, complete and confident handover.' },
  ],
  // Background photo for the process flow (real Willow kitchen detail)
  image: '/assets/images/home/modular-kitchen-walnut-panel-with-brass-tap-and-marble-counter.webp',
  imageAlt: 'Modular kitchen detail with walnut wall panel, brass tap, marble-look counter and induction hob',
};

// ─── SECTION 02 — PROJECTS ───────────────────────────────────────────────────

export const PROJECTS_SECTION = {
  sectionNumber: '02',
  sectionLabel: 'PROJECTS',
  h2: 'See What We Build.',
  body: 'Every project is different. The way we build it is what we work to make repeatable.',
  cta: { label: 'View All Projects →', href: '/projects' },
  // Interim descriptive titles (from what the photos show) until real project names,
  // localities and home types are added. Cards are portrait (3:4), so each uses a
  // portrait photo from that project's real-photo data.
  projects: [
    {
      name: 'Teal Kitchen & Walnut Bedrooms',
      slug: 'blue-on-mars-apartment-banashankari',
      location: 'Bengaluru',
      propertyType: 'Residential',
      shortDescription: 'Teal modular kitchen, walnut wardrobes, a foyer seating partition and a full-wall TV panel.',
      photo: 'foyer-seating-unit-with-arched-metal-partition-and-display-shelves.jpg',
    },
    {
      name: 'Sage & Wood Family Home',
      slug: 'sage-and-sunshine-apartment-sarjapur',
      location: 'Bengaluru',
      propertyType: 'Residential',
      shortDescription: 'Sage-green kitchen and crockery unit, fluted living-dining partitions and a terrazzo-panel wardrobe.',
      photo: 'sage-green-l-shaped-modular-kitchen-with-wooden-wall-cabinets.jpg',
    },
    {
      name: 'Wooden Kitchen & Lit Glass Wardrobes',
      slug: 'rasha-apartment-hosa-road-electronic-city',
      location: 'Bengaluru',
      propertyType: 'Residential',
      shortDescription: 'Modular kitchen, lit glass-shutter wardrobes, a Pichwai-style pooja unit and a study with pooja niche.',
      photo: 'black-frame-glass-shutter-wardrobe-with-led-lighting.jpg',
    },
    {
      name: GUBBI_GUDU.name,
      slug: GUBBI_GUDU.slug,
      location: 'Anjanapura, Bangalore',
      propertyType: 'Villa · G+4',
      shortDescription: GUBBI_GUDU.cardDescription,
      photo: 'gubbi-gudu-villa-living-room-with-walnut-tv-unit-and-fluted-wood-wall.jpg',
    },
  ].map(({ photo, ...p }) => {
    const found = findProjectPhoto(p.slug, photo);
    // Real name, place and summary from the written story, when one exists
    const story = PROJECT_STORIES[p.slug];
    const named = story
      ? { ...p, name: story.name, location: story.location.split(', ').slice(-2).join(', '), propertyType: story.propertyType.split(' · ')[0], shortDescription: story.cardDescription }
      : p;
    return { ...named, heroImage: found?.src ?? null, heroImageAlt: found?.alt };
  }) as Array<{
    name: string;
    slug: string;
    location: string;
    propertyType: string;
    shortDescription: string;
    heroImage?: string | null;
    heroImageAlt?: string;
  }>,
};

// ─── SECTION 03 — REVIEWS ────────────────────────────────────────────────────

export const REVIEWS_SECTION = {
  sectionNumber: '03',
  sectionLabel: 'CLIENTS',
  h2: 'What Our Clients Say.',
  googleReviewsUrl: null as string | null, // Set to real Google Business Profile URL when available
  // Real client reviews (several also on Google). Only spelling/capitalisation tidied — wording unchanged.
  // highlight = the one line a skimming visitor reads first; it must be a phrase from the review itself
  reviews: [
    {
      highlight: 'The kitchen was ready within 3 days of taking measurements.',
      text: 'Everything went on just the way they had projected themselves. The kitchen was ready within 3 days of taking measurements and unbelievable 2 hours of on site work. 0 onsite work, fast, neat and convenient. It is a near miracle to witness lightning fast service in this industry infamously known for deadline extension. Only if I knew it was this fast, would I have made a hyperlapse video of the same. Go with them blindly. BLINDLY. The founders are educated, have complete knowledge of the field and most importantly cultured! Also they handle the projects directly.',
      clientName: 'Pooja Rajak',
      projectLocation: '3BHK apartment, Varthur, Bangalore',
    },
    {
      highlight: 'Timeline was 40 days. They finished 2 weeks early.',
      text: 'Timeline was 40 days, they finished 2 weeks prior! Interiors look seamless, onsite work took 4 days. We are very happy with the designs, quality of work, the material they used and the finish. Dilip was the carpenter who worked at our site and he was very cooperative. We got all the curtain rods fitted.',
      clientName: 'Mr and Mrs Ganesh',
      projectLocation: 'Spandanam, Kerala',
    },
    {
      highlight: 'Hard to find honest and sincere people in interiors.',
      text: "I was lucky to have come across these guys and I mean this. It's hard to find honest and sincere people in general and especially in the field of interiors. I got my previous home done through a very famous company and waited for more than 8 months for the project to be completed. This time I was literally scared to get the interiors done. Only when I visited their factory unit, I was convinced yet was skeptical. But then I had to get the interiors done. I am glad to have chosen Willow Interiors.",
      clientName: 'Nandita',
      projectLocation: 'Independent home, Nelamangala',
    },
    {
      highlight: 'Completed in a week, and now our new home too.',
      text: "I am Shilpa Agrawal, a gynaecologist from Mumbai. Having entrusted them with the interior design of my rented apartment in Lodha Towers, Lower Parel, which they completed in a week, I'm now excited to collaborate with them once again for my new home in Lodha's latest block.",
      clientName: 'Dr. Shilpa Agrawal',
      projectLocation: 'Lodha, Lower Parel, Mumbai',
    },
  ] as Array<{
    highlight?: string;
    text: string;
    clientName?: string;
    projectLocation?: string;
  }>,
};

// ─── SECTION 04 — WHY WILLOW ─────────────────────────────────────────────────

export const WHY_WILLOW_SECTION = {
  sectionNumber: '04',
  sectionLabel: 'WHY WILLOW',
  h2: 'We Started With Manufacturing.',
  body1: 'Willow began with a manufacturing unit. Working on real interior projects exposed us to problems that repeatedly appeared between design and execution.',
  body2: 'Instead of accepting those problems as inevitable, we investigated them, documented what we learned and improved the way we build.',
  pillars: [
    {
      id: 'manufacturing',
      title: 'MANUFACTURING',
      description: 'We manufacture our modular products.',
    },
    {
      id: 'rd',
      title: 'R&D',
      description: 'We test materials and processes through real project work.',
    },
    {
      id: 'standardization',
      title: 'STANDARDIZATION',
      description: "Custom dimensions don't require a completely custom manufacturing method every time.",
    },
    {
      id: 'modularity',
      title: 'MODULARITY',
      description: 'Our products are designed with serviceability and replacement in mind.',
    },
    {
      id: 'accountability',
      title: 'ACCOUNTABILITY',
      description: 'When something goes wrong, we focus on fixing the system — not finding someone to blame.',
    },
  ],
};

// ─── SECTION 05 — MANUFACTURING ──────────────────────────────────────────────

export const MANUFACTURING_SECTION = {
  sectionNumber: '05',
  sectionLabel: 'MANUFACTURING',
  h2: 'Made in Our Own Manufacturing Facility.',
  body: 'Our manufacturing capability supports the modular interior products used across our projects, from kitchens and wardrobes to TV units, crockery units, beds, vanities and more.',
  statement: 'Manufacturing is not separate from our interior work. It is one of the foundations of how we execute it.',
  cta: { label: 'Explore Interior Manufacturing →', href: '/manufacturing' },
  process: [
    { id: 'material', label: 'MATERIAL' },
    { id: 'preparation', label: 'PREPARATION' },
    { id: 'machining', label: 'MACHINING' },
    { id: 'finishing', label: 'FINISHING' },
    { id: 'edge-banding', label: 'EDGE BANDING' },
    { id: 'assembly', label: 'ASSEMBLY' },
    { id: 'qc', label: 'QC' },
    { id: 'packaging', label: 'PACKAGING' },
  ],
};

// ─── SECTION 06 — MODULARITY ─────────────────────────────────────────────────

export const MODULARITY_SECTION = {
  sectionNumber: '06',
  sectionLabel: 'MODULARITY',
  h2: 'Modular Doesn\'t Just Mean Factory-Made.',
  body1: 'For us, modular means that components can be removed and replaced without unnecessarily damaging the surrounding system.',
  body2: 'That requires more than manufacturing furniture in a factory. It requires thinking about design, manufacturing, assembly, installation and future service as one system.',
  statement: 'Custom dimensions can still follow standardized manufacturing methods.',
  cta: { label: 'What Modular Interiors Mean at Willow →', href: '/modular-interiors' },
  diagramSteps: [
    { id: 'complete', label: 'COMPLETE PRODUCT', description: 'The finished installed unit' },
    { id: 'components', label: 'COMPONENTS', description: 'Individual manufactured parts' },
    { id: 'assembly', label: 'ASSEMBLY', description: 'How components connect' },
    { id: 'installed', label: 'INSTALLED', description: 'Fixed in your space' },
    { id: 'remove', label: 'REMOVE', description: 'A component can be taken out' },
    { id: 'replace', label: 'REPLACE', description: 'And replaced without damage' },
  ],
};

// ─── SECTION 07 — HOME INTERIORS / SCOPE ─────────────────────────────────────

export const HOME_INTERIORS_SECTION = {
  sectionNumber: '07',
  sectionLabel: 'HOME INTERIORS',
  h2: "What\'s Included in a Willow Project.",
  civilNote: 'Civil work can be coordinated as part of a project, but Willow does not directly execute civil construction.',
  renovationNote: 'Renovation projects are currently not undertaken.',
  cta: { label: 'What Complete Home Interiors Include →', href: '/home-interiors' },
  // icon: key into SCOPE_ICONS in page.tsx
  scopeItems: [
    { id: 'design', label: 'DESIGN', icon: 'design' },
    { id: 'modular-furniture', label: 'MODULAR FURNITURE', icon: 'wardrobe' },
    { id: 'false-ceiling', label: 'FALSE CEILING', icon: 'ceiling' },
    { id: 'electrical', label: 'ELECTRICAL', icon: 'bolt' },
    { id: 'plumbing', label: 'PLUMBING', icon: 'droplet' },
    { id: 'procurement', label: 'PROCUREMENT', icon: 'truck' },
    { id: 'installation', label: 'INSTALLATION', icon: 'tools' },
    { id: 'quality-check', label: 'QUALITY CHECK', icon: 'shield' },
    { id: 'handover', label: 'HANDOVER', icon: 'key' },
  ],
};

// ─── QUOTATION TRANSPARENCY ───────────────────────────────────────────────────

export const QUOTATION_SECTION = {
  h3: 'Compare Your Interior Quotations Properly.',
  body: 'Interior quotations can look very different even when they appear to describe the same project. Willow aims to prepare quotations in a format that helps customers understand what is included and compare the scope clearly.',
  cta: {
    label: 'Learn How to Compare Interior Quotations →',
    href: '/knowledge/how-to-compare-interior-quotations',
    pending: true, // Set to false when article is published
  },
};

// ─── SECTION 08 — R&D ────────────────────────────────────────────────────────

export const RD_SECTION = {
  sectionNumber: '08',
  sectionLabel: 'R&D',
  h2: 'We Decided Not to Make the Same Mistake Twice.',
  body1: 'Every project teaches us something.',
  body2: 'When a process fails, a material underperforms or an execution problem repeats, we investigate why, document what we learn and improve the system.',
  // EIS = light touch, plain words (what it means for the buyer); the full story lives on the Ennezz site
  eisStatement: 'The rules we built along the way became a written build standard (EIS), which is why our units last longer and can be repaired part by part.',
  eisUrl: null as string | null, // Set to real EIS URL when available
};

// ─── SECTION 09 — B2B ────────────────────────────────────────────────────────

export const B2B_SECTION = {
  sectionNumber: '09',
  sectionLabel: 'FOR PROFESSIONALS',
  h2: 'Have the Design? We Can Build It.',
  body: 'Willow works with interior designers, architects and builders who need manufacturing and execution infrastructure behind their projects.',
  collaborationNote: 'We work best with teams who want to be involved in how their design gets built.',
  cta: { label: 'Explore B2B Partnerships →', href: '/b2b' },
  cards: [
    {
      id: 'designers',
      title: 'INTERIOR DESIGNERS',
      description: 'You design. We build.',
      href: '/b2b/interior-designers',
    },
    {
      id: 'architects',
      title: 'ARCHITECTS',
      description: 'Your design intent. Our manufacturing expertise.',
      href: '/b2b/architects',
    },
    {
      id: 'builders',
      title: 'BUILDERS',
      description: 'Modular interiors built for project-scale execution.',
      href: '/contact',
    },
  ],
};

// ─── SECTION 10 — NRI ────────────────────────────────────────────────────────

export const NRI_SECTION = {
  sectionNumber: '10',
  sectionLabel: 'NRI',
  h2: 'Own a Home in Bangalore but Live Elsewhere?',
  body: 'Managing an interior project from outside Bangalore can make coordination harder. Willow brings design, manufacturing and installation into a coordinated process for homeowners managing projects in Bangalore from elsewhere.',
  cta: { label: 'Bangalore Interiors for NRIs →', href: '/nri-interiors-bangalore' },
  imageAlt: 'REAL WILLOW IMAGE REQUIRED — Willow Interiors project for NRI client in Bangalore',
};

// ─── SECTION 11 — FAQ ────────────────────────────────────────────────────────

export const FAQ_SECTION = {
  sectionNumber: '11',
  sectionLabel: 'QUESTIONS',
  h2: 'Questions We Hear Often.',
  items: [
    {
      question: 'What does a Willow interior project include?',
      answer:
        'A Willow project typically covers design, modular furniture manufacturing, false ceiling, electrical, plumbing, procurement, installation, quality checks and handover. Civil work can be coordinated as part of a project, but Willow does not directly execute civil construction.',
    },
    {
      question: 'Do you manufacture your own furniture?',
      answer:
        'Yes. Willow operates its own manufacturing facility in the Bangalore region. Modular products — including kitchens, wardrobes, TV units, crockery units, beds and vanities — are manufactured in-house.',
    },
    {
      question: 'What does modular mean at Willow?',
      answer:
        'For us, modular means that components can be removed and replaced without unnecessarily damaging the surrounding system. This requires thinking about design, manufacturing, assembly, installation and future service as one coordinated system.',
    },
    {
      question: 'Do you work with homeowners who already have a designer?',
      answer:
        'Yes. If you already have a designer or a completed design, Willow can provide manufacturing and installation support. We work with interior designers, architects and builders who need execution infrastructure behind their projects.',
    },
    {
      question: 'Do you undertake renovation projects?',
      answer: 'Renovation projects are currently not undertaken.',
    },
    {
      question: 'Can you manage a project if I am based outside Bangalore?',
      answer:
        'Willow brings design, manufacturing and installation into a coordinated process for homeowners managing projects in Bangalore from elsewhere. Contact us to discuss your specific situation.',
    },
    {
      question: 'How do I compare interior quotations from different companies?',
      answer:
        'Interior quotations can look very different even when they appear to describe the same project. Willow aims to prepare quotations in a format that helps customers understand what is included and compare the scope clearly. We are preparing a guide on this — contact us if you would like to discuss your quotation.',
    },
  ],
};

// ─── SECTION 12 — FINAL CTA ──────────────────────────────────────────────────

export const FINAL_CTA_SECTION = {
  h2: 'Planning Your Home Interiors in Bangalore?',
  body: "Tell us what you\'re planning. We\'ll start by understanding the project.",
  ctaPrimary: { label: 'START YOUR PROJECT', href: '#enquiry' },
  ctaWhatsApp: { label: 'WHATSAPP WILLOW' },
};
